<script setup>
import CardTitle from "@/Components/Links/CardTitle.vue";
import { getSvgForPlatform } from "@/services/iconService.js";
import { buildLinkAssetUrl } from "@/utils/assetUrls.js";
import { computed, onMounted, ref } from "vue";
const props = defineProps({
    link: { type: Object, required: true },
    appearance: { type: Object, default: () => ({}) },
    buttonStyle: { type: [Object, Array, String], default: () => ({}) },
    DoBucket: String,
    DoEndpoint: String,
    layout: { type: String, default: "classic" }, // 'classic' | 'featured'
    // When true, hide the left image in classic layout (used for slide-down previews)
    hideLeftImage: { type: Boolean, default: false },
    // When true, prevent the native navigation on anchor click (parent can handle it)
    preventNav: { type: Boolean, default: false },
});

const emit = defineEmits(["click", "share"]);

function onClick(e) {
    const shouldPrevent = props.preventNav;
    if (shouldPrevent) {
        // Stop the native navigation when explicitly requested by parent
        e?.preventDefault?.();
        e?.stopPropagation?.();
    }
    emit('click', props.link);
}

function onShareClick(e) {
    // Always prevent the anchor navigation and stop bubbling to the <a>
    e?.preventDefault?.();
    e?.stopPropagation?.();
    emit('share', props.link);
    console.log('share clicked');
}

// Resolve whether this link should behave as an embed (no navigation)
const isEmbedMode = computed(() => {
    try {
        const o = parsedOptions.value;
        // Common keys used across platforms
        const mode = o.mode || o.display_mode || o?.youtube?.display_mode || o?.x?.display_mode;
        if (!mode) return false;
        const m = String(mode).toLowerCase();
        return m === 'embed' || m === 'latest' || m === 'post_embed';
    } catch (e) {
        return false;
    }
});

// Compute final href. For embed modes, skip navigation altogether.
const hrefValue = computed(() => {
    if (isEmbedMode.value) return 'javascript:void(0)';
    return props.link?.url || props.link?.href || props.link?.link || '#';
});

// Determine current animation effect (buzz|wobble|pop|swipe)
const currentEffect = computed(() => {
    try {
        const p = parsedOptions.value?.prioritize;
        if (p?.type === "animation" && p?.animation_effect)
            return String(p.animation_effect);
        return "";
    } catch (e) {
        return "";
    }
});

// Add a one-time animate class on mount so the button animates without hover
const initialAnimateClass = ref("");
const onAnimEnd = () => {
    initialAnimateClass.value = "";
};

onMounted(() => {
    if (currentEffect.value) {
        initialAnimateClass.value = `${currentEffect.value}-animate`;
    }
});

// Normalize options — may arrive as a JSON string or a parsed object
const parsedOptions = computed(() => {
    const o = props.link?.options;
    if (!o) return {};
    if (typeof o === 'string') {
        try { return JSON.parse(o); } catch { return {}; }
    }
    return o;
});

// Icon display mode from options (undefined = legacy fallback)
const iconDisplay = computed(() => parsedOptions.value?.icon_display || null);
const iconStyle = computed(() => parsedOptions.value?.icon_style || 'color');
const linkFileUrl = computed(() =>
    buildLinkAssetUrl(props.DoEndpoint, props.DoBucket, props.link?.file),
);

// Get platform slug for icon lookup
function getPlatformSlug(link) {
    const raw = link?.platform || link?.options?.platform;
    if (!raw) return null;
    return String(raw).toLowerCase().replace(/\s+/g, "").replace(/\./g, "");
}

// Resolve the platform SVG based on icon_style preference
function getPlatformSvg(link) {
    const slug = getPlatformSlug(link);
    if (!slug) return null;
    const type = iconStyle.value === 'mono' ? 'mono' : 'default';
    return getSvgForPlatform(slug, type);
}
</script>

<template>
    <a
        :id="link.id"
        :href="hrefValue"
        :target="isEmbedMode ? null : '_blank'"
        :rel="isEmbedMode ? null : 'noopener noreferrer'"
        @click="onClick"
        class="btn align-items-center justify-content-between position-relative w-100 link-button"
        :class="[
            appearance.buttons || 'btn-light',
            appearance.button_margin_top || 'mt-0',
            parsedOptions?.prioritize?.type === 'animation' &&
            parsedOptions?.prioritize?.animation_effect
                ? parsedOptions.prioritize.animation_effect
                : '',
            initialAnimateClass,
            parsedOptions?.prioritize?.type === 'spotlight' ? 'spotlight' : '',
            layout, //  CSS
        ]"
        @animationend="onAnimEnd"
        :style="[
            buttonStyle,
            {
                '--hover-bg': appearance.button_background_hover || '#eee',
                '--hover-text': appearance.button_hover_color || '#fff',
                '--hover-transform': appearance.transform || 'scale(1.02)',
            },
        ]"
    >
        <!-- FEATURED LAYOUT -->
        <template v-if="layout === 'featured'">
            <div class="featured-wrap" :class="appearance.buttons">
                <div class="position-relative" style="aspect-ratio: 16/9">
                    <!-- Show uploaded file when explicit thumbnail mode or legacy fallback with file -->
                    <img
                        v-if="(iconDisplay === 'thumbnail' || !iconDisplay) && link.file"
                        :src="linkFileUrl"
                        alt="Thumbnail"
                        class="w-100 h-100 object-fit-cover"
                    />
                    <!-- Platform icon mode in featured: show SVG centered -->
                    <div
                        v-else-if="iconDisplay === 'platform' && getPlatformSvg(link)"
                        class="w-100 h-100 d-flex align-items-center justify-content-center featured-placeholder featured-platform-icon"
                        v-html="getPlatformSvg(link)"
                    ></div>
                    <!-- Placeholder for "none" mode or no content -->
                    <div
                        v-else
                        class="w-100 h-100 d-flex align-items-center justify-content-center featured-placeholder"
                    >
                        <!-- share icon -->
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="36"
                            height="36"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#6b7280"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <rect
                                x="3"
                                y="3"
                                width="18"
                                height="14"
                                rx="2"
                                ry="2"
                            ></rect>
                            <path d="M3 13l4-4 4 4 4-4 4 4"></path>
                        </svg>
                    </div>
                </div>
                <div
                    class="p-3 featured-bottom d-flex align-items-center dz-header-row"
                >
                    <!-- Title -->
                    <CardTitle
                        class="flex-grow-1 title"
                        :title="link.title || ''"
                        layout="featured"
                        :max="100"
                    />
                    <!-- Share Icon -->
                    <div class="ms-2">
                        <slot name="actions">
                            <svg
                                class="share-icon"
                                @click="onShareClick"
                                width="1.5rem"
                                height="1.5rem"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g id="SVGRepo_iconCarrier">
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        :fill="
                                            appearance.button_font_color ||
                                            '#000'
                                        "
                                        d="M16.5 2.25C14.7051 2.25 13.25 3.70507 13.25 5.5C13.25 5.69591 13.2673 5.88776 13.3006 6.07412L8.56991 9.38558C8.54587 9.4024 8.52312 9.42038 8.50168 9.43939C7.94993 9.00747 7.25503 8.75 6.5 8.75C4.70507 8.75 3.25 10.2051 3.25 12C3.25 13.7949 4.70507 15.25 6.5 15.25C7.25503 15.25 7.94993 14.9925 8.50168 14.5606C8.52312 14.5796 8.54587 14.5976 8.56991 14.6144L13.3006 17.9259C13.2673 18.1122 13.25 18.3041 13.25 18.5C13.25 20.2949 14.7051 21.75 16.5 21.75C18.2949 21.75 19.75 20.2949 19.75 18.5C19.75 16.7051 18.2949 15.25 16.5 15.25C15.4472 15.25 14.5113 15.7506 13.9174 16.5267L9.43806 13.3911C9.63809 12.9694 9.75 12.4978 9.75 12C9.75 11.5022 9.63809 11.0306 9.43806 10.6089L13.9174 7.4733C14.5113 8.24942 15.4472 8.75 16.5 8.75C18.2949 8.75 19.75 7.29493 19.75 5.5C19.75 3.70507 18.2949 2.25 16.5 2.25ZM14.75 5.5C14.75 4.5335 15.5335 3.75 16.5 3.75C17.4665 3.75 18.25 4.5335 18.25 5.5C18.25 6.4665 17.4665 7.25 16.5 7.25C15.5335 7.25 14.75 6.4665 14.75 5.5ZM6.5 10.25C5.5335 10.25 4.75 11.0335 4.75 12C4.75 12.9665 5.5335 13.75 6.5 13.75C7.4665 13.75 8.25 12.9665 8.25 12C8.25 11.0335 7.4665 10.25 6.5 10.25ZM16.5 16.75C15.5335 16.75 14.75 17.5335 14.75 18.5C14.75 19.4665 15.5335 20.25 16.5 20.25C17.4665 20.25 18.25 19.4665 18.25 18.5C18.25 17.5335 17.4665 16.75 16.5 16.75Z"
                                    />
                                </g>
                            </svg>
                        </slot>
                    </div>
                </div>
            </div>
        </template>

        <!-- CLASSIC LAYOUT -->
        <template v-else>
            <div
                class="d-flex align-items-center w-100 p-3 position-relative"
                :style="
                    hideLeftImage
                        ? { paddingRight: '36px' }
                        : { paddingRight: '36px' }
                "
            >
                <!-- Left Icon (classic): allow overriding via slot; otherwise default to file/platform/placeholder -->
                <div v-if="!hideLeftImage" class="left-icon me-2">
                    <slot name="left">
                        <!-- Explicit "none" hides the icon -->
                        <template v-if="iconDisplay === 'none'">
                            <div
                                class="placeholder-icon classic-icon d-flex align-items-center justify-content-center"
                                :class="appearance.buttons"
                            ></div>
                        </template>
                        <!-- Explicit "thumbnail" shows the uploaded file -->
                        <template v-else-if="iconDisplay === 'thumbnail' && link.file">
                            <img
                                :src="linkFileUrl"
                                alt="icon"
                                class="classic-icon"
                                :class="appearance.buttons"
                            />
                        </template>
                        <!-- Explicit "platform" shows the platform SVG with style preference -->
                        <template v-else-if="iconDisplay === 'platform' && getPlatformSvg(link)">
                            <div
                                class="classic-icon platform-svg-icon d-flex align-items-center justify-content-center"
                                :class="appearance.buttons"
                                v-html="getPlatformSvg(link)"
                            ></div>
                        </template>
                        <!-- Legacy fallback (no icon_display set): file > platform > placeholder -->
                        <template v-else-if="!iconDisplay && link.file">
                            <img
                                :src="linkFileUrl"
                                alt="icon"
                                class="classic-icon"
                                :class="appearance.buttons"
                            />
                        </template>
                        <template v-else-if="!iconDisplay && getSvgForPlatform(getPlatformSlug(link))">
                            <div
                                class="classic-icon platform-svg-icon d-flex align-items-center justify-content-center"
                                :class="appearance.buttons"
                                v-html="getSvgForPlatform(getPlatformSlug(link))"
                            ></div>
                        </template>
                        <template v-else>
                            <div
                                class="placeholder-icon classic-icon d-flex align-items-center justify-content-center"
                                :class="appearance.buttons"
                            ></div>
                        </template>
                    </slot>
                </div>
                <!-- If left image is hidden, keep layout balanced with spacer -->
                <div v-else class="left-spacer me-2"></div>

                <!-- Center Title -->
                <div
                    class="center flex-grow-1"
                    :class="{ 'text-center no-left-image': hideLeftImage }"
                    :style="{
                        maxWidth: hideLeftImage ? 'auto' : 'calc(100% - 80px)',
                    }"
                >
                    <slot name="title">
                        <CardTitle
                            :title="link.title || ''"
                            layout="classic"
                            align="center"
                            :max="100"
                        />
                    </slot>
                </div>

                <!-- Right Actions (share icon) -->
                <div class="right flex-shrink-0">
                    <slot name="actions">
                        <svg
                            class="share-icon"
                            @click="onShareClick"
                            width="1.5rem"
                            height="1.5rem"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <g id="SVGRepo_iconCarrier">
                                <path
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    :fill="
                                        appearance.button_font_color || '#000'
                                    "
                                    d="M16.5 2.25C14.7051 2.25 13.25 3.70507 13.25 5.5C13.25 5.69591 13.2673 5.88776 13.3006 6.07412L8.56991 9.38558C8.54587 9.4024 8.52312 9.42038 8.50168 9.43939C7.94993 9.00747 7.25503 8.75 6.5 8.75C4.70507 8.75 3.25 10.2051 3.25 12C3.25 13.7949 4.70507 15.25 6.5 15.25C7.25503 15.25 7.94993 14.9925 8.50168 14.5606C8.52312 14.5796 8.54587 14.5976 8.56991 14.6144L13.3006 17.9259C13.2673 18.1122 13.25 18.3041 13.25 18.5C13.25 20.2949 14.7051 21.75 16.5 21.75C18.2949 21.75 19.75 20.2949 19.75 18.5C19.75 16.7051 18.2949 15.25 16.5 15.25C15.4472 15.25 14.5113 15.7506 13.9174 16.5267L9.43806 13.3911C9.63809 12.9694 9.75 12.4978 9.75 12C9.75 11.5022 9.63809 11.0306 9.43806 10.6089L13.9174 7.4733C14.5113 8.24942 15.4472 8.75 16.5 8.75C18.2949 8.75 19.75 7.29493 19.75 5.5C19.75 3.70507 18.2949 2.25 16.5 2.25ZM14.75 5.5C14.75 4.5335 15.5335 3.75 16.5 3.75C17.4665 3.75 18.25 4.5335 18.25 5.5C18.25 6.4665 17.4665 7.25 16.5 7.25C15.5335 7.25 14.75 6.4665 14.75 5.5ZM6.5 10.25C5.5335 10.25 4.75 11.0335 4.75 12C4.75 12.9665 5.5335 13.75 6.5 13.75C7.4665 13.75 8.25 12.9665 8.25 12C8.25 11.0335 7.4665 10.25 6.5 10.25ZM16.5 16.75C15.5335 16.75 14.75 17.5335 14.75 18.5C14.75 19.4665 15.5335 20.25 16.5 20.25C17.4665 20.25 18.25 19.4665 18.25 18.5C18.25 17.5335 17.4665 16.75 16.5 16.75Z"
                                />
                            </g>
                        </svg>
                    </slot>
                </div>
            </div>
            <!-- Default slot area for expandable/inline content inside the button -->
            <div class="w-100">
                <slot></slot>
            </div>
        </template>
    </a>
</template>
<style>
/* clip children to wrapper's radius */
.featured-wrap {
    overflow: hidden;
}

/* bottom only; inherit radius from wrapper (so rounded-1..5 just work) */
.featured-bottom {
    border-top-left-radius: 0 !important;
    border-top-right-radius: 0 !important;
    border-bottom-left-radius: inherit !important;
    border-bottom-right-radius: inherit !important;
}
.classic-icon {
    width: 24px;
    height: 24px;
    object-fit: cover;
    border-radius: 6px; /* or 50% if you want circle */
    flex-shrink: 0;
}

.platform-svg-icon :deep(svg) {
    width: 100%;
    height: 100%;
}

.center {
    max-width: calc(100% - 80px); /* default with left image + right actions */
}

/* Fixed side widths to keep title truly centered when left image hidden */
.right {
    width: 28px;
    display: flex;
    justify-content: center;
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
}
.left-spacer {
    width: 28px;
    flex-shrink: 0;
}

.classic-title {
    display: block;
    white-space: normal; /* allow wrapping */
    word-wrap: break-word; /* break long words if needed */
    line-height: 1.3;
    font-size: 15px;
}

/* Placeholder styles */
.placeholder-icon {
    border-radius: 6px;
}
.featured-placeholder {
    background: #f8fafc; /* slate-50 */
}
.featured-platform-icon svg {
    width: 3rem;
    height: 3rem;
}

.featured-title {
    font-size: 15px;
    line-height: 1.4;
    white-space: normal; /* allow wrapping */
    word-wrap: break-word; /* break long words if needed */
    overflow: hidden; /* prevent overflow outside box */
}

.dz-header-row .title {
    flex: 1;
    text-align: center;
    padding: 0 8px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>
